/**
 * QueryAnswerCell — stacked Query / Answer lines for a single trace row.
 *
 * Renders only the Q and A content (no feedback — that lives in its own
 * sibling column so users can sort/filter on it). Used inside TraceTable
 * both on desktop (right-most column) and on mobile (inside the card body).
 *
 * Branches:
 *   - `error` present → collapse to a single red Error line.
 *   - `safetyRefused && !input && !output` → amber "refused" line.
 *   - `!input && !output` with an EVENT_LABEL match on `kind` → italic
 *     "something happened" phrase so event rows (jira_prefill_click,
 *     scheduled_brief, …) read as meaningful instead of blank.
 *   - `!input && !output` with no label → grey "No query/answer recorded".
 *   - Normal path → Q line (muted prompt label + text, clamped to 2 lines)
 *     followed by A line.
 *
 * Parent MUST render this inside a <TooltipProvider> — we only emit
 * <Tooltip> nodes and share the provider's delay config with peers
 * (FeedbackCell, etc.).
 */

import * as React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export const EVENT_LABEL: Record<string, string> = {
  jira_prefill_click: 'Jira prefill link opened (+5 min credit)',
  scheduled_brief: 'Scheduled brief delivered',
  brief_command: '/brief command executed',
  'article:publish': 'KB article published (+15 min credit)',
  'article:create': 'KB article drafted',
  'article:revise': 'KB article revised',
};

export interface QueryAnswerCellProps {
  input: string | null;
  output: string | null;
  error: string | null;
  kind: string;
  safetyRefused?: boolean;
}

export function QueryAnswerCell({
  input,
  output,
  error,
  kind,
  safetyRefused = false,
}: QueryAnswerCellProps): React.ReactElement {
  if (error) {
    return (
      <div className="flex flex-col gap-0.5 leading-snug">
        <TruncatedWithTooltip label="Error" text={error} variant="error" />
      </div>
    );
  }
  if (safetyRefused && !input && !output) {
    return (
      <div className="flex flex-col gap-0.5 leading-snug">
        <span className="text-amber-600 dark:text-amber-400 text-[11px] uppercase tracking-wide">
          refused
        </span>
      </div>
    );
  }
  if (!input && !output) {
    const eventLabel = EVENT_LABEL[kind];
    if (eventLabel) {
      return (
        <div className="flex flex-col gap-0.5 leading-snug">
          <span className="italic text-muted-foreground/80">{eventLabel}</span>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-0.5 leading-snug">
        <span className="italic text-muted-foreground/60">
          No query/answer recorded
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-0.5 leading-snug">
      {input ? (
        <TruncatedWithTooltip label="Q" text={input} variant="query" />
      ) : null}
      {output ? (
        <TruncatedWithTooltip label="A" text={output} variant="answer" />
      ) : (
        <span className="italic text-muted-foreground/60 text-[11px]">
          (no answer recorded)
        </span>
      )}
    </div>
  );
}

function TruncatedWithTooltip({
  label,
  text,
  variant,
}: {
  label: string;
  text: string;
  variant: 'query' | 'answer' | 'error';
}): React.ReactElement {
  const labelClass =
    variant === 'error'
      ? 'text-destructive'
      : variant === 'query'
        ? 'text-muted-foreground'
        : 'text-foreground/80';
  const textClass =
    variant === 'error'
      ? 'text-destructive'
      : variant === 'answer'
        ? 'text-foreground/90'
        : 'text-muted-foreground';
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <div className="flex min-w-0 items-baseline gap-1.5 cursor-default">
          <span
            aria-hidden="true"
            className={`shrink-0 font-semibold tabular-nums text-[10px] uppercase tracking-wide ${labelClass}`}
          >
            {label}
          </span>
          <span
            className={`line-clamp-2 min-w-0 flex-1 break-words ${textClass}`}
          >
            {text}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="left"
        align="start"
        sideOffset={8}
        collisionPadding={16}
        // `text-left` overrides the default centered-text on TooltipContent
        // (see `components/ui/tooltip.tsx`). Multi-line Q / A / Error
        // previews are prose — centering them looked like stanza formatting
        // and wrapped awkwardly on long strings.
        className="w-[50vw] max-w-[50vw] max-h-[70vh] overflow-y-auto whitespace-pre-wrap break-words text-left text-xs leading-relaxed"
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}
